<template>
    <layout>
        <main class="setup-page">
            <section class="header">
                <div class="messaging info">
                    <p>
                        New and Improved Seis Brace® Seismic Software with Anvil and AFCON Products. Compliant with NFPA 13-2022 bracing requirements.
                    </p>
                    <p>
                        For questions, please contact your local ASC representative or call <a href="tel:8446926845">844-692-6845</a>.
                    </p>
                    <div class="images">
                        <img src="https://s3.us-east-2.amazonaws.com/asc-es.com/images/logos/asc_anvil_rgb_pos_sm.png" alt="Anvil logo">
                        <img src="https://s3.us-east-2.amazonaws.com/asc-es.com/images/logos/asc_afcon_rgb_pos_sm.png" alt="AFCON">
                        <img src="https://s3.us-east-2.amazonaws.com/asc-es.com/images/certifications/culus.png" alt="UL Listed">
                        <img src="https://s3.us-east-2.amazonaws.com/asc-es.com/images/certifications/fm-approved.png" alt="FM Approved">
                    </div>
                </div>
                <h1>1. Seismic Design Setup</h1>
                <p>Enter information for your Seismic design below.</p>
            </section>

            <loading v-if="loading"></loading>

            <section class="setup">
                <div v-if="!loading">
                    <fieldset>
                        <div class="text">
                            <label class="required" for="project-name">
                                Seismic Design Name
                            </label>
                            <input type="text" v-model="record.name" id="project-name" placeholder="Name your design" required>
                            <errors :errors="errors" prop="name"></errors>
                        </div>
                    </fieldset>

                    <fieldset>
                        <div class="text">
                        <label class="required" for="project-date">
                            Project Date
                        </label>
                        <input type="date" v-model="dateOnly" id="project-date" required>
                        <span v-if="dateError" class="error">{{ dateError }}</span>
                        <errors :errors="errors" prop="dateCreated"></errors>
                        </div>
                    </fieldset>

                    <!-- Hidden field to store the datetime format -->
                    <input type="hidden" v-model="record.dateCreated">

                    <fieldset class="standard">
                        <label class="required">
                            Code Requirements
                        </label>
                        <div class="select">
                            <select v-model="record.standardId" @change="changeStandardId" required>
                                <option :value="standard.id" v-text="standard.name" v-for="standard in standards"></option>
                            </select>
                        </div>
                        <errors :errors="errors" prop="standardId"></errors>
                    </fieldset>

                    <fieldset class="setup">
                        <label>Project Units</label>
                        <div class="select">
                            <select v-model="record.useMetric">
                                <option value="0">Imperial (ft, in, lbs, lbf, psi)</option>
                                <option value="1">Metric (m, mm, kg, kN, MPa)</option>
                            </select>
                        </div>
                    </fieldset>

                    <fieldset class="agency" ref="agency">
                        <label class="required" >Pick UL or FM loads</label>
                        <div>
                            <div class="select">
                                <select v-model="record.agency" required>
                                    <option value="fm-approved">FM Approved</option>
                                    <option value="ul-listed">UL Listed</option>
                                </select>
                            </div>
                            <errors :errors="errors" prop="agency"></errors>
                            <div v-if="showAgencyRequiredError" class="messaging error simple">
                                <p>
                                    Agency is required. Please select an option before proceeding.
                                </p>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset class="flex notes add-more">
                        <label>Note 1</label>
                        <div>
                            <div class="textarea">
                                <textarea v-model="record.notes1"></textarea>
                            </div>
                            <div>
                                <p class="note">
                                    Use the note section to be specific about any special detailed
                                    information regarding this seismic project submittal.
                                </p>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset class="add-more" v-if="showAdditionalNote">
                        <div class="textarea">
                            <label>Note 2</label>
                            <textarea v-model="record.notes2"></textarea>
                        </div>
                    </fieldset>

                    <div v-if="!showAdditionalNote" class="actions">
                        <button class="simple" @click="toggleAdditionalNote()">
                            <i class="fa fa-plus"></i>
                            Add Additional Note
                        </button>
                    </div>
                </div>
            </section>

            <section v-if="!loading">
                <h1>Contacts</h1>

                <div class="contacts" ref="contacts">
                    <contact v-for="(contact, index) in contacts" :key="contact.uid" :id="contact.id" v-on:sync-contact="syncContact" v-on:editing-contact="changedContacts=true" v-on:delete-contact="deleteContact" :uid="contact.uid" ref="contact"></contact>
                </div>

                <div class="actions">
                    <button class="simple" @click="addContact()">
                        <i class="fa fa-plus"></i>
                        Add Contact
                    </button>
                </div>
            </section>

            <section v-if="!loading">
                <actions
                    @next="saveThenNext"
                    @save="saveThenSummary"
                    ></actions>
            </section>

        </main>

        <aside class="primary">
            <sidebar :design="this.record"></sidebar>
        </aside>
    </layout>
</template>

<script>
    import {mapMutations, mapGetters} from 'vuex';
    import Layout from './layout';
    import Contact from './contact';

    import Errors from './shared/errors';
    import Sidebar from './shared/sidebar';
    import Actions from './shared/actions';

    import ShortUniqueId from 'short-unique-id';
    import Vue from 'vue';
    import ClockvineMixin from 'vue/common/clockvine-mixin';

    const uid = new ShortUniqueId();

    export default {
        props: [
            'projectId'
        ],

        components: {
            Layout,
            Sidebar,
            Contact,
            Actions,
            Errors,
        },

        mixins: [ClockvineMixin],

        data: function() {
            return {
                type: 'submittalDesign',
                record: this.baseRecord(),
                contacts: [],
                additionalNoteToggled: false,
                saveButtonFinishedText: 'Saved!',
                changedContacts: false,
                showAgencyRequiredError: false,
                dateError: '',
                dateOnly: '',
            };
        },

        watch: {
            // Watch for changes to record.dateCreated and initialize dateOnly accordingly
            'record.dateCreated': function(newVal) {
            this.initializeDate();
            }
        },
        mounted() {
            if (this.exists) {
                this.$store.dispatch('submittalContact/index', { designId: this.$options.propsData.id })
                    .then(() => {
                        let contacts = this.$store.state.submittalContact.indexes[`/actions/seismic-submittal/contact/index?designId=${this.id}`];
                        contacts.forEach(c => this.addContact(c));
                        this.changedContacts = false;
                    });

                this.$store.dispatch('submittalBrace/reindex', { designId: this.$options.propsData.id });
            }

            // Initialize dateOnly from record.dateCreated if available, otherwise use current date
            this.initializeDate();
        },

        computed: {
            ...mapGetters({
                standards: 'submittalStandards/standards',
            }),
            braces() {
                return this.$store.state.submittalBrace.indexes[`/actions/seismic-submittal/brace/index?designId=${this.id}`] || [];
            },
            hasBraces() {
                return this.braces.length > 0;
            },
            nextButtonText() {
                return this.hasBraces ? 'Save and View Summary' : 'Next';
            },
            showAdditionalNote() {
                return this.additionalNoteToggled || this.record.notes2;
            },
            hasChanges() {
                return this.changedContacts || this.isDirty();
            },
        },

        methods:  {
            ...mapMutations('submittalStandards', ['storeId']),

            changeStandardId() {
                this.storeId(this.record.standardId);

                this.$store.dispatch('submittalPipes/retrievePipes', this.record.standardId).then(() => {
                    this.$store.dispatch('submittalSystem/calculateWeights');
                });
            },

            toggleAdditionalNote() {
                this.additionalNoteToggled = true;
            },

            baseRecord() {
                return {
                    projectId: this.projectId || null,
                };
            },

            syncContact(data) {
                let index = this.contacts.findIndex(i => i.uid === data.uid);

                this.contacts[index] = data;
            },

            addContact(data = {}) {
                this.changedContacts = true;

                this.contacts.push({
                    id: null,
                    uid: uid.randomUUID(6),
                    ...data
                });
            },

            deleteContact(uid) {
                this.changedContacts = true;

                this.contacts = this.contacts.filter((item) => {
                    return item.uid !== uid;
                });
            },

            getRecordParams() {
                let record = Object.assign({}, this.record, {
                    contacts: this.contacts,
                });

                return Object.assign({}, this.getUrlParams(), { data: record });
            },

            validateDate() {
                if (!this.dateOnly) {
                    const now = new Date();
                    const yyyy = now.getFullYear();
                    const mm = String(now.getMonth() + 1).padStart(2, '0');
                    const dd = String(now.getDate()).padStart(2, '0');
                    this.dateOnly = `${yyyy}-${mm}-${dd}`;
                }

                const dateParts = this.dateOnly.split('-');
                if (dateParts.length !== 3) {
                    this.dateError = 'The project date must be a valid date.';
                    return false;
                }

                const yyyy = parseInt(dateParts[0], 10);
                const mm = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
                const dd = parseInt(dateParts[2], 10);

                const date = new Date(Date.UTC(yyyy, mm, dd));

                // Set to 00:00:00
                const hh = '00';
                const min = '00';
                const ss = '00';

                // Convert date to Y-m-d H:i:s format
                this.record.dateCreated = `${yyyy}-${String(mm + 1).padStart(2, '0')}-${String(dd).padStart(2, '0')} ${hh}:${min}:${ss}`;
                this.dateError = '';
                return true;
            },
            initializeDate() {
                if (this.record.dateCreated) {
                    const date = new Date(this.record.dateCreated);
                    const yyyy = date.getFullYear();
                    const mm = String(date.getMonth() + 1).padStart(2, '0');
                    const dd = String(date.getDate()).padStart(2, '0');
                    this.dateOnly = `${yyyy}-${mm}-${dd}`;
                } else {
                    const now = new Date();
                    const yyyy = now.getFullYear();
                    const mm = String(now.getMonth() + 1).padStart(2, '0');
                    const dd = String(now.getDate()).padStart(2, '0');
                    this.dateOnly = `${yyyy}-${mm}-${dd}`;
                }
            },

            validateAgency() {
                // if no agency is selected on an existing record, show custom error
                // if a new record, only show model validation required error
                if (!this.record.agency && this.record.id) {
                    this.showAgencyRequiredError = true;
                    this.$refs.agency.scrollIntoView({ behavior: "smooth" });
                    return false;
                }

                return true;
            },

            saveThenSummary() {
                if (!this.validateAgency() || !this.validateDate()) {
                    return;
                }

                this.save().then(() => {
                    this.$store.dispatch('submittalContact/reindex', { designId: this.$options.propsData.id });
                    this.closeContactForms();
                    this.$router.push({name: 'submittal-brace-summary', params: { id: this.record.id }});
                });

            },

            saveThenNext() {
                if (!this.validateAgency() || !this.validateDate()) {
                    return;
                }

                this.save().then(() => {
                    this.$store.dispatch('submittalContact/reindex', { designId: this.$options.propsData.id });
                    this.closeContactForms();
                    this.$router.push({name: 'submittal-brace-new', params: { designId: this.record.id }});
                });
            },

            closeContactForms() {
                if (this.$refs.contact) {
                    this.$refs.contact.forEach((component) => { component.closeForm() });
                }
            },
        },
    };
</script>
